import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Inbox, Search, SearchX } from 'lucide-react'
import { EmptyState } from '../radix'
import { AdminEnhancedPagination } from './AdminEnhancedPagination'
import { AdminOrderStatusPill } from './AdminOrderStatusPill'
import { OrdersTableSkeleton } from './OrdersTableSkeleton'
import { useAllTenantOrders } from '../../auth/hooks/useSupplierChanges'
import { formatCurrency } from '../../utils/currency'
import { splitDateTime } from '../../utils/datetime'
import type { AllTenantOrder } from '../../auth/types'
import {
  QUIET_BUTTON,
  SUPPLIER_CHANGES_DEFAULT_PAGE_SIZE,
  SUPPLIER_CHANGES_SEARCH_DEBOUNCE_MS,
} from '../../constants/supplierChanges'

// The SaaS admin's "All orders" tab (PO-008 AC1), as the mock's Option J:
// every company's orders, newest first, with search, and Option D's page bar.
// Read-only: a row opens the order's own page.

const TH = 'whitespace-nowrap px-4 py-2.5 text-left text-xs font-semibold text-gray-600'
const ROW_LINK = 'group relative border-b border-gray-100 align-top transition-colors last:border-b-0 hover:bg-gray-50'
const ROW_ANCHOR =
  "font-semibold text-gray-900 underline-offset-2 group-hover:underline focus-visible:outline-none after:absolute after:inset-0 after:content-[''] focus-visible:after:rounded-sm focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-brand-primary"

export function AllOrdersTab() {
  const [searchQuery, setSearchQuery] = useState('')
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(SUPPLIER_CHANGES_DEFAULT_PAGE_SIZE)

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchQuery.trim())
      setCurrentPage(1)
    }, SUPPLIER_CHANGES_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const { data, isLoading, error } = useAllTenantOrders({ search: search || undefined, page: currentPage, pageSize: itemsPerPage })
  const orders = data?.results ?? []
  const totalItems = data?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))

  return (
    <div className="space-y-3">
      <div className="relative w-full sm:w-80">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden="true" />
        <label htmlFor="all-orders-search" className="sr-only">Search orders</label>
        <input id="all-orders-search" type="search" placeholder="Order, engineer, tenant or branch" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} className="h-9 w-full rounded-lg border border-gray-300 bg-white pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary" />
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6">
          <h3 className="mb-2 font-bold text-red-900">Failed to load orders</h3>
          <p className="text-sm text-red-700">{error.message || 'Please try again later'}</p>
        </div>
      ) : isLoading ? (
        <OrdersTableSkeleton />
      ) : orders.length === 0 ? (
        <div className="rounded-[16px] border-2 border-gray-200 bg-white">
          {search ? (
            <EmptyState
              icon={<SearchX className="h-10 w-10 text-gray-400" aria-hidden="true" />}
              title="No orders match your filters"
              description="Try adjusting your search or filters."
              action={<button type="button" onClick={() => setSearchQuery('')} className={QUIET_BUTTON}>Clear filters</button>}
            />
          ) : (
            <EmptyState icon={<Inbox className="h-10 w-10 text-gray-400" aria-hidden="true" />} title="No orders found" description="No orders yet." />
          )}
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" aria-label="All orders">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th scope="col" className={TH}>Order</th>
                    <th scope="col" className={TH}>Engineer</th>
                    <th scope="col" className={TH}>Supplier</th>
                    <th scope="col" className={TH}>Status</th>
                    <th scope="col" className={`${TH} text-right`}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => <OrderRow key={order.id} order={order} />)}
                </tbody>
              </table>
            </div>
          </div>
          <AdminEnhancedPagination currentPage={Math.min(currentPage, totalPages)} totalPages={totalPages} itemsPerPage={itemsPerPage} totalItems={totalItems} onPageChange={setCurrentPage} onItemsPerPageChange={(value) => { setItemsPerPage(value); setCurrentPage(1) }} />
        </>
      )}
    </div>
  )
}

function OrderRow({ order }: { order: AllTenantOrder }) {
  const { tenantSlug } = useParams()
  const placed = splitDateTime(order.created_at)
  const changes = order.changed_line_count
  return (
    <tr className={ROW_LINK}>
      <td className="px-4 py-3">
        {/* The order page loads the order from the company in its address. */}
        <Link to={`/${order.tenant_slug ?? tenantSlug}/orders/${order.id}`} className={ROW_ANCHOR}>{order.order_number}</Link>
        <div className="text-xs text-gray-500">Placed {placed.date}{placed.time ? `, ${placed.time}` : ''}</div>
      </td>
      <td className="px-4 py-3">
        <div className="text-gray-900">{order.engineer_name}</div>
        <div className="text-xs text-gray-500">{order.tenant_name}</div>
      </td>
      <td className="px-4 py-3">
        <div className="text-gray-900">{order.supplier_name}</div>
        {order.branch_name && <div className="text-xs text-gray-500">{order.branch_name} branch</div>}
      </td>
      <td className="px-4 py-3">
        <AdminOrderStatusPill status={order.status} />
        {changes > 0 && <div className="mt-1 text-xs text-gray-600">{changes} supplier change{changes === 1 ? '' : 's'}</div>}
      </td>
      <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-900">{formatCurrency(parseFloat(order.total_amount), 2)}</td>
    </tr>
  )
}
